import {
	dateColumn,
	dateTimeColumn,
	enumColumn,
	floatColumn,
	integerColumn,
	moneyColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { MenuCheckbox } from "src/components/common/contextMenu/MenuCheckbox.tsx";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { faFileImport } from "@fortawesome/pro-regular-svg-icons";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import {
	ParamsWithDataModelRequest,
	ServerSideDataGridModel,
	ServerSideDataGridModelRenderProps,
} from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel.tsx";
import { FormCrudDataGrid, FormCrudDataGridModelProps } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { ProjectActivityMaterialForm } from "src/components/views/erp/project/projectActivity/material/ProjectActivityMaterialForm.tsx";
import { logError } from "src/errorHandling/errorLogging.ts";
import {
	ProjectActivityMaterialsDataGridApi_DataGridData,
	ProjectActivityMaterialsDataGridApi_ProjectActivityMaterialDto,
} from "src/api/generated/erp/project/projectActivityMaterial/api/projectActivityMaterialsDataGridApi.ts";
import {
	getProjectActivityMaterialStateLabel,
	getProjectActivityMaterialStateLabels,
	ProjectActivityMaterialState,
	ProjectActivityMaterialStateValues,
} from "src/api/generated/erp/db/types/enums/projectActivityMaterialState.ts";
import {
	AcquisitionMethod,
	AcquisitionMethodValues,
	getAcquisitionMethodLabel,
} from "src/api/generated/erp/db/types/enums/acquisitionMethod.ts";
import { getPurchaseOrderLineStateLabel } from "src/api/generated/erp/db/types/enums/purchaseOrderLineState.ts";
import { getShopOrderStateLabel } from "src/api/generated/erp/db/types/enums/shopOrderState.ts";
import { formatDayJs, parseIsoDateNullable } from "src/utils/dayjsUtils.ts";
import { ProjectActivityMaterialRowContextMenu } from "./ProjectActivityMaterialRowContextMenu.tsx";
import { AavoSelectField } from "../../../../../common/inputFields/AavoSelectField.tsx";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { GridRowParams } from "@mui/x-data-grid/models";
import { HorizontalBox } from "src/components/common/box/HorizontalBox";
import { StaticDataGridModel } from "src/components/common/dataGrid/gridModel/StaticDataGridModel.tsx";
import { useState } from "react";
import { SubProject } from "src/api/generated/erp/db/types/tables/subProject.ts";
import { ProjectActivity } from "src/api/generated/erp/db/types/tables/projectActivity.ts";
import { getOnlyMember } from "src/utils/arrayUtils.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { uploadFile } from "src/utils/fileUploading.ts";
import { ProjectActivityMaterialTabularImportApi } from "src/api/generated/erp/project/projectActivityMaterial/importing/projectActivityMaterialTabularImportApi.ts";
import { VertexToProjectActivityMaterialApi } from "src/api/generated/erp/project/projectActivityMaterial/importing/vertexToProjectActivityMaterialApi.ts";
import { SolidWorksToProjectActivityMaterialApi } from "src/api/generated/erp/project/projectActivityMaterial/importing/solidWorksToProjectActivityMaterialApi.ts";
import { WinnerImportApi } from "src/api/generated/erp/integrations/winner/api/winnerImportApi.ts";
import { ImportFromVertexIndButton } from "src/components/views/erp/integrations/vertexInd/ImportFromVertexIndButton.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { AavoMultiSelectField } from "src/components/common/inputFields/AavoMultiSelectField.tsx";
import {
	getLineFilterModeLabel,
	LineFilterMode,
	LineFilterModeValues,
} from "src/api/generated/erp/project/projectActivityMaterial/lineFilterMode.ts";
import { aavoGridDetailPanelToggleCol } from "src/components/common/dataGrid/masterDetail/aavoGridDetailPanelToggleCol.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ProjectActivityMaterialsDataGridBaseProps {
	gridId: string;
	fetchData: (
		args: ParamsWithDataModelRequest<FetchDataParams>,
	) => Promise<ProjectActivityMaterialsDataGridApi_DataGridData>;
	showActivityColumn?: boolean;
	showSubProjectColumn?: boolean;
	showLineNumberColumn?: boolean;
	showSubProjectFilter?: boolean;
}

interface FetchDataParams {
	showCancelledLines: boolean;
	subProjectId: number | null | undefined;
	searchQuery: string;
	stateFilter: ProjectActivityMaterialState[];
	lineFilterMode: LineFilterMode;
	acquisitionMethodFilter: AcquisitionMethod[];
}

export const ProjectActivityMaterialsDataGridBase = ({
	gridId,
	fetchData,
	...otherProps
}: ProjectActivityMaterialsDataGridBaseProps) => {
	// Represents all selected rows, regardless if they are in master or detail data grid.
	const [globalSelectedRows, setGlobalSelectedRows] = useState<
		ProjectActivityMaterialsDataGridApi_ProjectActivityMaterialDto[]
	>([]);

	const topLevelSelectedRows = globalSelectedRows.filter((row) => row.parentMaterialId == null);

	return (
		<ServerSideDataGridModel
			fetchData={fetchData}
			initialParams={{
				showCancelledLines: false,
				subProjectId: genericNullableValue<number>(),
				searchQuery: "",
				stateFilter: Array<ProjectActivityMaterialState>(),
				lineFilterMode: "SHOW_ALL" as LineFilterMode,
				acquisitionMethodFilter: Array<AcquisitionMethod>(),
			}}
			storedParams={["lineFilterMode"]}
			getDataModelResult={(data) => data.data}
			getRowId={(row) => row.projectActivityMaterialId}
			customFunctionUiActionView={"PROJECT_ACTIVITY_MATERIALS"}
			gridId={gridId + "_v9"}
			selectedRows={topLevelSelectedRows}
			onSelectionChanged={setGlobalSelectedRows}
			render={({ data, ...otherParams }) => (
				<ProjectActivityMaterialsDataGridInner
					{...otherParams}
					activity={data.activity}
					subProjectOptions={data.subProjectOptions}
					gridId={gridId}
					globalSelectedRows={globalSelectedRows}
					setGlobalSelectedRows={setGlobalSelectedRows}
					parentMaterialId={null}
					{...otherProps}
				/>
			)}
		/>
	);
};

interface ProjectActivityMaterialsDataGridInnerProps
	extends
		Omit<ProjectActivityMaterialsDataGridBaseProps, "fetchData">,
		Omit<
			ServerSideDataGridModelRenderProps<
				ProjectActivityMaterialsDataGridApi_DataGridData,
				ProjectActivityMaterialsDataGridApi_ProjectActivityMaterialDto,
				FetchDataParams
			>,
			"data" | "dataGridProps"
		> {
	dataGridProps: FormCrudDataGridModelProps<ProjectActivityMaterialsDataGridApi_ProjectActivityMaterialDto>;
	activity: ProjectActivity | undefined | null;
	subProjectOptions: SubProject[];
	globalSelectedRows: ProjectActivityMaterialsDataGridApi_ProjectActivityMaterialDto[];
	setGlobalSelectedRows: (rows: ProjectActivityMaterialsDataGridApi_ProjectActivityMaterialDto[]) => void;
	parentMaterialId: number | null | undefined;
	hideActionBar?: boolean;
}

const ProjectActivityMaterialsDataGridInner = (props: ProjectActivityMaterialsDataGridInnerProps) => {
	const {
		dataGridProps,
		showActivityColumn = false,
		showSubProjectColumn = false,
		showLineNumberColumn,
		showSubProjectFilter,
		refreshData,
		currentParams,
		globalSelectedRows,
		activity,
		subProjectOptions,
		hideActionBar,
		parentMaterialId,
	} = props;

	const { tenantConfig } = useTenantCustomizations();
	const showInputDialog = useInputDialog();
	const importMethods = tenantConfig?.erp?.projectActivityMaterialImportMethods ?? [];

	return (
		<FormCrudDataGrid<ProjectActivityMaterialsDataGridApi_ProjectActivityMaterialDto>
			columns={[
				aavoGridDetailPanelToggleCol<ProjectActivityMaterialsDataGridApi_ProjectActivityMaterialDto>(
					(row) => row.childLines.length > 0,
				),
				showSubProjectColumn &&
					textColumn({
						field: "subProjectDescription",
						headerName: i18n.t("sub_project"),
						width: 150,
					}),
				showActivityColumn &&
					textColumn({
						field: "activityName",
						headerName: i18n.t("activity"),
						width: 150,
					}),
				showLineNumberColumn &&
					integerColumn({
						field: "lineNumber",
						headerName: i18n.t("line_no"),
					}),
				textColumn({
					field: "configurationOrPartDescription",
					headerName: i18n.t("part"),
					width: 200,
				}),
				floatColumn({
					field: "quantityInBaseUnit",
					headerName: i18n.t("quantity"),
				}),
				floatColumn({
					field: "reservedQuantityInBaseUnit",
					headerName: i18n.t("reserved_quantity"),
				}),
				floatColumn({
					field: "pickedQuantityInBaseUnit",
					headerName: i18n.t("picked_quantity"),
				}),
				floatColumn({
					field: "deliveredQuantityInBaseUnit",
					headerName: i18n.t("delivered_quantity"),
				}),
				textColumn({
					field: "partUnit",
					headerName: i18n.t("base_unit"),
				}),
				moneyColumn({
					field: "unitCost",
					headerName: i18n.t("unit_cost"),
				}),
				textColumn({
					field: "acquisitionMethod",
					headerName: i18n.t("acquisition_method"),
					width: 250,
					valueGetter: (_, row) => {
						const methodLabel = getAcquisitionMethodLabel(row.acquisitionMethod);
						const purchaseOrderStateLabel =
							row.purchaseOrderLineState != null ?
								getPurchaseOrderLineStateLabel(row.purchaseOrderLineState)
							:	null;
						const shopOrderStateLabel =
							row.shopOrderState != null ? getShopOrderStateLabel(row.shopOrderState) : null;
						const purchaseOrderArrivalDate =
							row.purchaseOrderLinePlannedArrivalDate != null ?
								formatDayJs(parseIsoDateNullable(row.purchaseOrderLinePlannedArrivalDate))
							:	null;
						return (
							row.shopOrderId != null ?
								`${methodLabel} (${[row.shopOrderId, shopOrderStateLabel]
									.filter((value) => value != null && value !== "")
									.join(", ")})`
							: row.purchaseOrderId != null ?
								`${methodLabel} (${[
									row.purchaseOrderId,
									purchaseOrderStateLabel,
									purchaseOrderArrivalDate,
								]
									.filter((value) => value != null && value !== "")
									.join(", ")})`
							:	`${methodLabel}`
						);
					},
				}),
				textColumn({
					field: "mark",
					headerName: i18n.t("mark"),
				}),
				dateColumn({
					field: "demandDate",
					headerName: i18n.t("demand_date"),
				}),
				dateTimeColumn({
					field: "releasedAt",
					headerName: i18n.t("ordered_at"),
					width: 150,
				}),
				enumColumn({
					field: "state",
					headerName: i18n.t("state"),
					enumLabels: getProjectActivityMaterialStateLabels(),
				}),
				dateTimeColumn({
					field: "cancelledAt",
					headerName: i18n.t("cancelled_at"),
				}),
				textColumn({
					field: "supplierName",
					headerName: i18n.t("supplier"),
				}),
				textColumn({
					field: "shopOrderBatchCode",
					headerName: i18n.t("shop_order_batch_code"),
				}),
				integerColumn({
					field: "projectActivityMaterialId",
					headerName: i18n.t("id"),
				}),
			]}
			hideActionBar={hideActionBar}
			actionBarMenuComponents={[
				<MenuCheckbox
					key={"showCancelled"}
					label={i18n.t("show_cancelled")}
					checked={currentParams.showCancelledLines}
					onChange={(checked) => refreshData({ showCancelledLines: checked })}
				/>,
				activity != null && importMethods.includes("STANDARD_TABULAR") && (
					<AsyncMenuButton
						key={"importTabular"}
						label={i18n.t("import_lines")}
						icon={faFileImport}
						onClick={async () => {
							const file = await showInputDialog({
								type: "singleFile",
								title: i18n.t("select_a_file"),
								defaultValue: null,
								required: true,
								fieldProps: {
									file: {
										accept: ".csv,.tsv,text/csv,text/tab-separated-values,application/vnd.ms-excel",
									},
								},
							});
							if (file == null) return;
							const fileHandle = await uploadFile(file);
							await ProjectActivityMaterialTabularImportApi.importLines({
								projectActivityId: activity.activityId,
								fileHandle: fileHandle,
							});
							await refreshData();
						}}
					/>
				),
				activity != null && importMethods.includes("VERTEX") && (
					<ImportFromVertexIndButton
						key={"importFromVertex"}
						submit={async (fileHandle) => {
							await VertexToProjectActivityMaterialApi.importVertexFileToActivityMaterials({
								projectActivityId: activity.activityId,
								fileHandle: fileHandle,
							});
							await refreshData();
						}}
					/>
				),
				activity != null && importMethods.includes("SOLIDWORKS") && (
					<AsyncMenuButton
						label={i18n.t("import_from_solidworks")}
						icon={faFileImport}
						onClick={async () => {
							const file = await showInputDialog({
								title: i18n.t("select_solidworks_file"),
								type: "singleFile",
								defaultValue: null,
								required: true,
								fieldProps: {
									file: {
										accept: "text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
									},
								},
							});
							if (file == null) return;
							const fileHandle = await uploadFile(file);
							await SolidWorksToProjectActivityMaterialApi.importSolidWorksFileToActivityMaterial({
								projectActivityId: activity!.activityId,
								fileHandle: fileHandle,
							});
							await refreshData();
						}}
					/>
				),
				activity != null && importMethods.includes("WINNER") && (
					<AsyncMenuButton
						label={i18n.t("import_from_winner")}
						icon={faFileImport}
						onClick={async () => {
							const file = await showInputDialog({
								title: i18n.t("select_winner_file"),
								type: "singleFile",
								defaultValue: null,
								required: true,
							});
							if (file == null) return;
							const fileHandle = await uploadFile(file);
							await WinnerImportApi.importWinnerDesignsToProjectActivityMaterials({
								projectActivityId: activity!.activityId,
								winnerExportFileHandle: fileHandle,
							});
							await refreshData();
						}}
					/>
				),
			]}
			actionBarComponents={
				<>
					<DocumentsOfObjectButton
						objectRef={nullableAavoObjectRef(
							"PROJECT_ACTIVITY_MATERIAL",
							getOnlyMember(globalSelectedRows)?.projectActivityMaterialId,
						)}
					/>
					<AavoSelectField
						label={i18n.t("line_filter")}
						options={[...LineFilterModeValues]}
						getOptionKey={(option: LineFilterMode) => option}
						getOptionLabel={(option) => getLineFilterModeLabel(option)}
						value={currentParams.lineFilterMode}
						disableClearable
						onChange={(_, lineFilterMode) => refreshData({ lineFilterMode })}
					/>
					<AavoTextField label={i18n.t("search")} onSubmit={(searchQuery) => refreshData({ searchQuery })} />
					<AavoMultiSelectField
						label={i18n.t("state")}
						options={ProjectActivityMaterialStateValues}
						getOptionKey={(option) => option}
						getOptionLabel={(option) => getProjectActivityMaterialStateLabel(option)}
						onChange={(stateFilter) => refreshData({ stateFilter })}
					/>
					<AavoMultiSelectField
						label={i18n.t("acquisition_method")}
						options={AcquisitionMethodValues}
						getOptionKey={(option) => option}
						getOptionLabel={(option) => getAcquisitionMethodLabel(option)}
						onChange={(acquisitionMethodFilter) => refreshData({ acquisitionMethodFilter })}
					/>
					{showSubProjectFilter && (
						<AavoSelectField
							label={i18n.t("sub_project")}
							options={subProjectOptions}
							getOptionKey={(option) => option.subProjectId}
							getOptionLabel={(option) => option.subProjectDescription}
							value={currentParams.subProjectId}
							onChange={(subProjectId) => refreshData({ subProjectId })}
						/>
					)}
				</>
			}
			rowContextMenuComponents={(rowContextMenuParams) => (
				<ProjectActivityMaterialRowContextMenu
					key={"contextMenu"}
					{...rowContextMenuParams}
					refreshData={refreshData}
				/>
			)}
			form={{
				editEnabled: true,
				addRowEnabled: activity != null && ["INITIAL", "RELEASED", "STARTED"].includes(activity.activityState),
				dialogSize: "md",
				dialogTitle: i18n.t("material_line"),
				agentContext: (row) => ({
					key: defineAgentUiContextKey("erp.project.projectActivityMaterial.formDialog"),
					title: i18n.t("material_line"),
					pathSegments: row == null ? undefined : [`${i18n.t("line_no")} ${row.lineNumber}`],
					data: {
						activityId: activity?.activityId ?? row?.activityId ?? null,
						projectActivityMaterialId: row?.projectActivityMaterialId ?? null,
						parentMaterialId: parentMaterialId ?? null,
					},
				}),
				component: ({ row, ...other }) => {
					const activityIdToUse = activity?.activityId ?? row?.activityId;
					if (activityIdToUse == null) {
						logError("Form opened but activityId is null");
						return;
					}
					return (
						<ProjectActivityMaterialForm
							activityId={activityIdToUse}
							materialLineId={row?.projectActivityMaterialId}
							{...other}
						/>
					);
				},
				// Override default edit behavior because there's only one edit button
				// but multiple data grids with conflicting internal selection state.
				customEditAction: ({ openFormForRow }) => {
					const rowToEdit = getOnlyMember(globalSelectedRows);
					if (rowToEdit == null) {
						return {
							type: "disabled",
							tooltip: i18n.t("select_a_single_row"),
						};
					}
					return {
						type: "enabled",
						action: () => {
							openFormForRow(rowToEdit);
						},
					};
				},
			}}
			getDetailPanelContent={(params) => <DetailPanel {...props} {...params} />}
			agentContext={{
				key: defineAgentUiContextKey("erp.project.projectActivityMaterials.grid"),
				data: {
					activityId: activity?.activityId ?? null,
					parentMaterialId: parentMaterialId ?? null,
				},
				rowFields: ["projectActivityMaterialId", "activityId", "subProjectId", "partId", "partConfigurationId"],
				selectedRowPathSegment: (row) => `${i18n.t("line_no")} ${row.lineNumber}`,
			}}
			{...dataGridProps}
		/>
	);
};

interface DetailPanelContentProps
	extends
		ProjectActivityMaterialsDataGridInnerProps,
		GridRowParams<ProjectActivityMaterialsDataGridApi_ProjectActivityMaterialDto> {}

const DetailPanel = ({
	row: parentRow,
	globalSelectedRows,
	setGlobalSelectedRows,
	...otherProps
}: DetailPanelContentProps) => {
	if (parentRow.childLines.length === 0) return null;

	const childLinesMapped: ProjectActivityMaterialsDataGridApi_ProjectActivityMaterialDto[] = parentRow.childLines.map(
		(childLine) => ({
			...childLine,
			childLines: [],
		}),
	);

	const selectedRowsForThisParent = globalSelectedRows.filter(
		(row) => row.parentMaterialId == parentRow.projectActivityMaterialId,
	);

	return (
		<HorizontalBox
			sx={{
				margin: 2,
				marginLeft: 6,
			}}
		>
			<StaticDataGridModel
				rows={childLinesMapped}
				gridId={otherProps.gridId + "_detail"}
				getRowId={(row) => row.projectActivityMaterialId}
				customFunctionUiActionView={"PROJECT_ACTIVITY_MATERIALS"}
				selectedRows={selectedRowsForThisParent}
				onSelectionChanged={setGlobalSelectedRows}
				render={({ dataGridProps, selectedRows, onlySelectedRow, setSelectedRows, getApi }) => (
					<ProjectActivityMaterialsDataGridInner
						{...otherProps}
						showSubProjectColumn={false}
						showActivityColumn={false}
						showSubProjectFilter={false}
						globalSelectedRows={globalSelectedRows}
						setGlobalSelectedRows={setGlobalSelectedRows}
						selectedRows={selectedRows}
						onlySelectedRow={onlySelectedRow}
						setSelectedRows={setSelectedRows}
						parentMaterialId={parentRow.projectActivityMaterialId}
						hideActionBar
						getApi={getApi}
						dataGridProps={{
							...dataGridProps,
							refreshData: otherProps.refreshData,
							rowCount: childLinesMapped.length,
						}}
					/>
				)}
			/>
		</HorizontalBox>
	);
};
